import * as React from "react";
import {
    DataGridPremium,
    GridColDef,
    GridColumnGroup,
    GridColumnGroupingModel,
    GridRenderCellParams,
} from "@mui/x-data-grid-premium";
import { Alert, Tooltip, Typography } from "@mui/material";
import { Stack } from "@mui/system";
import AssessmentStatusGroupHeader from "./assessmentStatusGroupHeader";
import SelfStatusCell, { SelfStatusCellProps } from "./selfStatusCell";
import {
    ParticipantAssessmentStatus,
    SessionInstrument,
    SessionInstrumentRaterInfo,
} from "../../../../../../services/types/enterpriseParticipantApiTypes";
import RaterStatusCell from "./raterStatusCell";

export interface InstrumentVisibility {
    visible: boolean;
    id: number;
    name: string;
}

export interface AssessmentStatusDataGridPdfProps {
    instruments: SessionInstrument[];
    participants: ParticipantAssessmentStatus[];
    sessionKey: number | undefined;
    sessionCode: string | undefined;
}

const AssessmentStatusDataGridPdf: React.FC<AssessmentStatusDataGridPdfProps> = (props) => {
    const [columnViewStatus, setColumnViewStatus] = React.useState<InstrumentVisibility[]>([]);

    const raterColWidthWide = 100;
    const raterColWidthNarrow = 100;
    const selfAndScoreColWidth = 114;

    function getRaterColumnId(instrumentId: number, raterCol: SessionInstrumentRaterInfo): string {
        return `${instrumentId}.${raterCol.raterTypeName.replace(/\s+/g, "").toLowerCase()}`;
    }

    const colDefs: GridColDef[] = React.useMemo(() => {
        let columns: GridColDef[] = [];

        if (
            props?.instruments != null &&
            props.instruments.length > 0 &&
            columnViewStatus.length > 0
        ) {
            columns = columns.concat([
                {
                    field: "participant",
                    headerAlign: "left",
                    headerName: "Participant",
                    type: "string",
                    groupingValueGetter: (params) => {
                        return `${params.row.firstName} ${params.row.lastName}`;
                    },
                    valueGetter: (params) => {
                        return `${params.row.firstName} ${params.row.lastName}`;
                    },
                    renderCell: (params: GridRenderCellParams) => {
                        return params.value;
                    },
                    maxWidth: 275,
                    minWidth: 150,
                    flex: 1.0,
                },
            ]);

            [...props.instruments]
                .sort((a, b) => {
                    if (a.hasRaters && !b.hasRaters) {
                        return 1;
                    } else if (!a.hasRaters && b.hasRaters) {
                        return -1;
                    } else {
                        return a.name.localeCompare(b.name);
                    }
                })
                .forEach((inst, index) => {
                    if (inst.hasRaters) {
                        // add the columns we know we need
                        columns = columns.concat([
                            {
                                field: `${inst.id}.self`,
                                headerAlign: "center",
                                headerName: "Self",
                                headerClassName: "superHeader",
                                cellClassName: "whiteCell",
                                type: "string",
                                renderCell: (params: GridRenderCellParams) => {
                                    if (params.row.instruments.length > 0) {
                                        return (
                                            <SelfStatusCell
                                                {...getSelfStatusParams(params.row, inst.id)}
                                            />
                                        );
                                    }
                                    return null;
                                },
                                maxWidth: selfAndScoreColWidth,
                                minWidth: selfAndScoreColWidth,
                                disableColumnMenu: true,
                                sortable: false,
                                filterable: false,
                            },
                        ]);
                        const raters =
                            props.instruments.find((i) => i.id === +inst.id)?.raters ?? [];

                        [...raters]
                            .filter((r) => r != null)
                            .forEach((r, index) => {
                                if (r.raterTypeId !== 1) {
                                    columns.push({
                                        field: getRaterColumnId(inst.id, r),
                                        headerAlign: "center",
                                        renderHeader: () => {
                                            return (
                                                <Tooltip
                                                    title={
                                                        <div>
                                                            <Typography sx={{ fontSize: "1.25em" }}>
                                                                Minimum: {r.minimumRequired}
                                                            </Typography>
                                                            <Typography sx={{ fontSize: "1.25em" }}>
                                                                Recommended: {r.minimumRecommended}
                                                            </Typography>
                                                        </div>
                                                    }
                                                >
                                                    <div
                                                        style={{
                                                            lineHeight: "1.5em",
                                                        }}
                                                    >
                                                        <span
                                                            style={{
                                                                justifyContent: "center",
                                                                display: "flex",
                                                            }}
                                                        >
                                                            {r?.raterTypeName}
                                                        </span>
                                                        <span
                                                            style={{
                                                                justifyContent: "center",
                                                                display: "flex",
                                                            }}
                                                        >{`${r.minimumRequired}/${r.minimumRecommended}`}</span>
                                                    </div>
                                                </Tooltip>
                                            );
                                        },
                                        headerClassName: "greyCellHeader",
                                        align: "left",
                                        cellClassName: "greyCell",
                                        type: "string",
                                        renderCell: (params: GridRenderCellParams) => {
                                            if (params.row.instruments.length > 0) {
                                                return (
                                                    <RaterStatusCell
                                                        participant={params.row}
                                                        instrumentId={inst.id}
                                                        raterId={r.raterTypeId}
                                                        isWide={r?.raterTypeName.length > 10}
                                                    />
                                                );
                                            }
                                            return null;
                                        },
                                        minWidth:
                                            r?.raterTypeName.length > 10
                                                ? raterColWidthWide
                                                : raterColWidthNarrow,
                                        maxWidth:
                                            r?.raterTypeName.length > 10
                                                ? raterColWidthWide
                                                : raterColWidthNarrow,
                                        disableColumnMenu: true,
                                        sortable: false,
                                        filterable: false,
                                    });
                                }
                            });
                    } else {
                        columns = columns.concat([
                            {
                                field: `${inst.id}.self`,
                                headerAlign: "center",
                                headerName: "Self",
                                headerClassName: "superHeader",
                                cellClassName: "whiteCell",
                                type: "string",
                                renderCell: (params: GridRenderCellParams) => {
                                    if (params.row.instruments.length > 0) {
                                        return (
                                            <SelfStatusCell
                                                {...getSelfStatusParams(params.row, inst.id)}
                                            />
                                        );
                                    }
                                    return null;
                                },
                                maxWidth: selfAndScoreColWidth,
                                minWidth: selfAndScoreColWidth,
                                disableColumnMenu: true,
                                sortable: false,
                                filterable: false,
                            },
                        ]);
                    }
                });
        }
        return columns;
    }, [props, columnViewStatus.length]);

    const generateVisibilityModel = React.useCallback(() => {
        let newVisibilityModel = {};
        props.instruments.forEach((inst) => {
            const viewStatus = columnViewStatus.find((i) => i.id === inst.id);

            Object.assign(newVisibilityModel, {
                actions: false,
                [`${inst.id}.self`]: viewStatus != null ? viewStatus.visible : true,
            });
            if (inst.hasRaters) {
                const raters = inst.raters.filter((r) => r.raterTypeName.toLowerCase() !== "self");

                Object.assign(newVisibilityModel, {
                    [`${inst.id}.scorable`]: viewStatus?.visible ?? true,
                });
                if (raters.length > 0) {
                    raters.forEach((raterCol) => {
                        const raterColId = getRaterColumnId(inst.id, raterCol);
                        Object.assign(newVisibilityModel, {
                            [raterColId]: viewStatus?.visible ?? true,
                        });
                    });
                }
            }
        });
        return newVisibilityModel;
    }, [props.instruments, columnViewStatus]);

    React.useEffect(() => {
        setColumnViewStatus(
            props.instruments.map((i) => {
                return {
                    visible: true,
                    id: i.id,
                    name: i.name,
                };
            })
        );
    }, [columnViewStatus, generateVisibilityModel, props.instruments]);

    const getSelfStatusParams = (
        pax: ParticipantAssessmentStatus,
        instrumentId: number
    ): SelfStatusCellProps => {
        const paxInstrument = pax.instruments.find((i) => i.instrumentId === +instrumentId);
        const st = paxInstrument?.selfAssessmentStatusTypeId ?? 0;
        const numRaters = paxInstrument?.supportedRaterTypeCount ?? 0;
        const scrd =
            paxInstrument?.scoringStatus == null ? false : paxInstrument.scoringStatus === 2;
        return { status: st, hasRaters: numRaters > 1, scored: scrd };
    };

    let colGroupingModel: GridColumnGroupingModel = [];

    if (props.instruments.length > 0) {
        let columnGroup: GridColumnGroup[] = props.instruments.map((inst, index) => {
            let children = [{ field: `${inst.id}.self` }];
            if (inst.hasRaters) {
                const raters = props.instruments.find((i) => i.id === +inst.id)?.raters ?? [];

                raters.forEach((r) => {
                    children.push({
                        field: getRaterColumnId(inst.id, r),
                    });
                });
            }

            return {
                groupId: `${inst.shortName}-${inst.id}`,
                children: children,
                headerAlign: "center",
                headerClassName: "superHeader",
                renderHeaderGroup: (params) => {
                    return (
                        <AssessmentStatusGroupHeader
                            {...params}
                            headerText={inst.hasRaters ? inst.name : inst.shortName}
                            hoverTitle={`${inst.name} (${inst.shortName})`}
                        />
                    );
                },
            };
        });
        colGroupingModel = columnGroup.sort((a: GridColumnGroup, b: GridColumnGroup) =>
            a.groupId.localeCompare(b.groupId)
        );
    }

    if (
        props.instruments == null ||
        props.instruments.length === 0 ||
        props.participants == null ||
        props.participants.length === 0
    ) {
        return (
            <Stack width={1} spacing={2}>
                <Alert severity="info">
                    No assessment status information found for this session.
                </Alert>
            </Stack>
        );
    }
    const sortedParticipants = [...props.participants].sort((a, b) =>
        a.firstName.localeCompare(b.firstName)
    );

    return (
        <Stack>
            <DataGridPremium
                getRowId={(row) => row.esiKey}
                experimentalFeatures={{ columnGrouping: true }}
                columns={colDefs}
                rows={sortedParticipants}
                disableSelectionOnClick={true}
                columnGroupingModel={colGroupingModel}
                sx={{
                    "& .MuiDataGrid-columnSeparator": {
                        visibility: "hidden",
                    },
                    "& .greyCell": {
                        backgroundColor: "#F0F1F3",
                        paddingLeft: "2px",
                    },
                    "& .moreBtnCell": {
                        paddingLeft: "0px",
                        paddingRight: "0px",
                    },
                    "& .greyCellHeader": {
                        backgroundColor: "#F0F1F3",
                        borderLeft: "1px solid #E0E0E0",
                        paddingLeft: "2px",
                        paddingRight: "2px",
                        fontWeight: "bold",
                    },
                    "& .superHeader": {
                        borderLeft: "1px solid #E0E0E0",
                        fontWeight: "bold",
                    },
                    "& .whiteCell": {
                        paddingLeft: "4px",
                    },
                    "& .MuiDataGrid-pinnedColumns": {
                        backgroundColor: "#FFFFFF",
                    },
                    "& .MuiDataGrid-pinnedColumnHeaders": {
                        backgroundColor: "#FFFFFF",
                    },
                }}
                hideFooter={true}
                autoHeight
                initialState={{
                    sorting: { sortModel: [{ field: "name", sort: "asc" }] },
                    pinnedColumns: {
                        left: ["__check__", "participant", "more-buttons", "actions"],
                    },
                }}
                disableRowGrouping
                disableExtendRowFullWidth
            />
        </Stack>
    );
};

export default AssessmentStatusDataGridPdf;
